'use client'

import { useState } from 'react'
import Link from 'next/link'
import { ArrowRight, ChevronDown, Copy, Check, ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { track } from '@/lib/analytics/track'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'

interface SidebarHowToProps {
  slug: string
  skillTitle: string
  promptText?: string
}

export function SidebarHowTo({ slug, skillTitle, promptText }: SidebarHowToProps) {
  const [showMore, setShowMore] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!promptText) return
    await navigator.clipboard.writeText(promptText)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="rounded-xl border bg-muted/40 p-6">
      <h3 className="text-muted-foreground mb-4 text-sm font-semibold tracking-wider uppercase">
        How to Use
      </h3>

      {/* Option B: Copy & paste (default, always visible) */}
      <div>
        <p className="text-sm font-semibold">
          Use with any AI tool
        </p>
        <ol className="mt-3 space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              1
            </span>
            <div className="text-muted-foreground">
              <p>Copy the full skill prompt.</p>
              {promptText && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary transition-colors hover:bg-primary/20"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      Click to Copy
                    </>
                  )}
                </button>
              )}
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              2
            </span>
            <p className="text-muted-foreground">
              Paste it as the first message in ChatGPT, Claude, or any AI chat.
            </p>
          </li>
          <li className="flex gap-3">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[11px] font-bold text-primary-foreground">
              3
            </span>
            <p className="text-muted-foreground">
              Follow the AI&apos;s questions — it will guide you step by step and
              deliver the result.
            </p>
          </li>
        </ol>
      </div>

      {/* Collapsible: More ways */}
      <Collapsible open={showMore} onOpenChange={setShowMore}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="mt-5 inline-flex w-full items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <ChevronDown
              className={`h-3.5 w-3.5 transition-transform ${showMore ? 'rotate-180' : ''}`}
            />
            More ways to use this skill
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="mt-4 space-y-5 border-t pt-4">
            {/* Option C: Claude Projects */}
            <div>
              <p className="text-sm font-semibold">
                Save as a Claude skill
              </p>
              <ol className="mt-2 space-y-1.5 text-xs text-muted-foreground">
                <li>
                  1. Copy the prompt and open{' '}
                  <a
                    href="https://claude.ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-foreground"
                  >
                    claude.ai
                  </a>
                </li>
                <li>
                  2. Paste with: &ldquo;Save this as a reusable skill
                  called {skillTitle}&rdquo;
                </li>
                <li>
                  3. Claude saves it — reuse anytime by typing the skill
                  name.
                </li>
              </ol>
            </div>

            {/* Option D: Claude Code CLI */}
            <div>
              <p className="text-sm font-semibold">
                Install via Claude Code
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                If you use{' '}
                <a
                  href="https://docs.anthropic.com/en/docs/claude-code"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground"
                >
                  Claude Code
                </a>
                , install skills as slash commands:
              </p>
              <div className="mt-2 rounded-md bg-muted px-3 py-2">
                <code className="text-xs">
                  npx awesome-marketing-skills install{' '}
                  <span className="text-muted-foreground">&lt;skill-name&gt;</span>
                </code>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Read more link */}
      <a
        href="https://github.com/About-Intelligence/awesome-marketing-skills"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ExternalLink className="h-3 w-3" />
        Browse all skills on GitHub
      </a>

      {/* Primary CTA */}
      <Button
        size="lg"
        className="mt-5 h-12 w-full px-6 text-base shadow-lg transition-shadow hover:shadow-xl"
        asChild
      >
        <Link
          href="/sign-up"
          onClick={() => track('skill_detail_cta_click', { slug, cta_location: 'sidebar' })}
        >
          Try this Skill in Soku AI
          <ArrowRight className="ml-2 h-5 w-5" />
        </Link>
      </Button>
    </div>
  )
}
